import { XMLParser } from 'fast-xml-parser';

/**
 * A parsed XML element.
 *
 * fast-xml-parser is configured with `preserveOrder` because OOXML is
 * order-sensitive in several places that matter — z-order in a shape tree,
 * colour transforms, path commands, runs inside a paragraph — and the default
 * object output silently loses all of it. The array-of-single-key-objects shape
 * that gives us is awkward to read, so it is normalised into `Element` once,
 * here, rather than in every parser.
 */
export class Element {
  constructor(
    readonly name: string,
    readonly attrs: Readonly<Record<string, string>>,
    readonly children: readonly Element[],
    readonly text: string,
  ) {}

  /** First child with this qualified name, or undefined. */
  child(name: string): Element | undefined {
    return this.children.find((c) => c.name === name);
  }

  /** First descendant reached by walking the given names in order. */
  path(...names: readonly string[]): Element | undefined {
    return names.reduce<Element | undefined>(
      (node, name) => node?.child(name),
      this as Element,
    );
  }

  all(name: string): Element[] {
    return this.children.filter((c) => c.name === name);
  }

  /** Children matching any of the given names, in document order. */
  anyOf(names: readonly string[]): Element[] {
    const set = new Set(names);
    return this.children.filter((c) => set.has(c.name));
  }

  has(name: string): boolean {
    return this.children.some((c) => c.name === name);
  }

  attr(name: string): string | undefined {
    return this.attrs[name];
  }

  /** Attribute as an integer. Returns `fallback` when absent or unparseable. */
  int(name: string, fallback: number): number {
    const raw = this.attrs[name];
    if (raw === undefined) return fallback;
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : fallback;
  }

  num(name: string, fallback: number): number {
    const raw = this.attrs[name];
    if (raw === undefined) return fallback;
    const n = Number.parseFloat(raw);
    return Number.isFinite(n) ? n : fallback;
  }

  /** OOXML booleans are `1`/`0` as often as `true`/`false`. */
  bool(name: string, fallback: boolean): boolean {
    const raw = this.attrs[name];
    if (raw === undefined) return fallback;
    return raw === '1' || raw === 'true' || raw === 'on';
  }

  /** Text content of this element and its descendants. */
  innerText(): string {
    if (this.children.length === 0) return this.text;
    return this.text + this.children.map((c) => c.innerText()).join('');
  }

  /** Depth-first walk, this element included. */
  *walk(): Generator<Element> {
    yield this;
    for (const child of this.children) yield* child.walk();
  }

  find(predicate: (el: Element) => boolean): Element | undefined {
    for (const el of this.walk()) {
      if (predicate(el)) return el;
    }
    return undefined;
  }
}

const ATTR_PREFIX = '@_';
const TEXT_KEY = '#text';
const ATTR_KEY = ':@';

const parser = new XMLParser({
  preserveOrder: true,
  ignoreAttributes: false,
  attributeNamePrefix: ATTR_PREFIX,
  parseAttributeValue: false,
  parseTagValue: false,
  trimValues: false,
  processEntities: true,
  // Namespace prefixes are load-bearing in OOXML (`a:` vs `p:` vs `r:`), so they
  // are kept rather than stripped.
  removeNSPrefix: false,
});

interface RawNode {
  [key: string]: unknown;
}

/**
 * Parses an XML document into a single root element.
 *
 * Never throws on malformed input: an unparseable part yields an empty element
 * named `#invalid`, which every caller treats as "nothing here", so one corrupt
 * part cannot cost the author the rest of the deck.
 */
export function parseXml(xml: string): Element {
  let raw: unknown;
  try {
    raw = parser.parse(xml);
  } catch {
    return new Element('#invalid', {}, [], '');
  }
  if (!Array.isArray(raw)) return new Element('#invalid', {}, [], '');
  const elements = toElements(raw as RawNode[]);
  // Skip the XML declaration node fast-xml-parser emits as `?xml`.
  const root = elements.find((e) => e.name !== '?xml');
  return root ?? new Element('#empty', {}, [], '');
}

export function parseXmlBytes(bytes: Uint8Array): Element {
  return parseXml(decodeUtf8(bytes));
}

function toElements(nodes: readonly RawNode[]): Element[] {
  const out: Element[] = [];
  for (const node of nodes) {
    const attrs = (node[ATTR_KEY] as Record<string, string> | undefined) ?? {};
    for (const [key, value] of Object.entries(node)) {
      if (key === ATTR_KEY) continue;
      if (key === TEXT_KEY) continue;
      const childNodes = Array.isArray(value) ? (value as RawNode[]) : [];
      out.push(
        new Element(key, stripAttrPrefix(attrs), toElements(childNodes), directText(childNodes)),
      );
    }
  }
  return out;
}

function directText(nodes: readonly RawNode[]): string {
  let text = '';
  for (const node of nodes) {
    const value = node[TEXT_KEY];
    if (typeof value === 'string') text += value;
    else if (typeof value === 'number') text += String(value);
  }
  return text;
}

function stripAttrPrefix(attrs: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(attrs)) {
    out[key.startsWith(ATTR_PREFIX) ? key.slice(ATTR_PREFIX.length) : key] = String(value);
  }
  return out;
}

export function decodeUtf8(bytes: Uint8Array): string {
  return new TextDecoder('utf-8').decode(bytes);
}
